import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../../components/ui/button";
import {
  FaExclamationTriangle,
  FaEye,
  FaPencilAlt,
  FaPlus,
  FaSave,
  FaTimes,
  FaTrashAlt,
} from "react-icons/fa";
import { useParams } from "react-router-dom";
import { DataTable } from "../../components/DataTable";
import {
  groupService,
  membershipService,
  membershipDataMap,
  ServiceOnMount,
} from "../../services";
import { Skeleton } from "../../components/ui/skeleton";
import ProfileCard from "../../components/ProfileCard";
import Modal from "../../components/Modal";
import ShortenedText from "../../components/ShortenedText";
import useUserStore2 from "../../stores/userStore2";
import useUserStore from "../../stores/userStore";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { invitationService } from "../../services/invitationService";
import { Input } from "../../components/ui/input";
import { createPageStore } from "../../stores/pages/pageStore";
import { GoPeople } from "react-icons/go";
import MultiEmailSelect from "../../components/ChangeSelect";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import StatusBadge from "../../components/table/StatusBadge";
dayjs.extend(relativeTime);

const columns = [
  {
    accessorKey: "profileId",
    header: "Member ID",
    cell: ({ row }) => {
      if (!row.getValue("profileId")) return <span>Invitation sent</span>;
      return (
        <span>
          <ShortenedText text={row.getValue("profileId")} />
        </span>
      );
    },
  },
  {
    accessorKey: "profileName",
    header: "Member Name",
    cell: ({ row }) => <span>{row.getValue("profileName")}</span>,
  },
  {
    accessorKey: "email",
    header: "Email Address",
    cell: ({ row }) => <span>{row.getValue("email")}</span>,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status");
      return (
        <StatusBadge
          status={
            status === "Deactivated" && !row.original.verified
              ? "Invitation sent"
              : status
          }
        />
      );
    },
  },
  {
    accessorKey: "lastLogin",
    header: "Last Login",
    cell: ({ row }) => (
      <span>
        {
          row.getValue("lastLogin")
            ? dayjs(row.getValue("lastLogin")).fromNow()
            : "never"
          // new Date(row.getValue("lastLogin")).toLocaleString()
        }
      </span>
    ),
  },
];

/**
 * @typedef {Object} MembershipItem
 * @property {string} membershipId - The unique ID of the membership
 * @property {string} communityId - The ID of the community the membership belongs to
 * @property {string} profileId - The ID of the user profile, or "N/A" if not available
 * @property {string} profileName - The name of the user profile, or "N/A" if not available
 * @property {string} email - The email address of the entity, or "N/A" if not available
 * @property {string} status - The status of the membership: "Active", "Suspended", or "Pending"
 * @property {string} lastLogin - The formatted date and time of the last login, or "N/A" if not available
 * @property {string} groupName - The name of the group, or "N/A" if not available
 * @property {string} groupDateCreated - The formatted date when the group was created, or "N/A" if not available
 * @property {string} groupDateUpdated - The formatted date when the group was last updated, or "N/A" if not available
 * @property {string} dateCreated - The formatted date when the membership was created, or "N/A" if not available
 * @property {string} dateUpdated - The formatted date when the membership was last updated, or "N/A" if not available
 * @property {string} groupId - The ID of the group, or "N/A" if not available
 * @property {string} groupLeader - The names of the group leaders joined by commas, or "N/A" if not available
 */
/**
 * @typedef {Object} MembershipsTable
 * @property {MembershipItem[]} membershipsTable
 */

/**
 * @typedef {Object.<string, MembershipItem[]>} MembershipsByGroup
 * A mapping of group IDs to arrays of membership items
 * @example
 * {
 *   "groupId1": [
 *     {
 *       "membershipId": "membershipId1",
 *       "communityId": "communityId1",
 *       // ... other membership properties
 *     },
 *     // ... other memberships for groupId1
 *   ],
 *   "groupId2": [
 *     // ... memberships for groupId2
 *   ]
 * }
 */

/** @type {MembershipsByGroup} */
const initialState = {
  membershipsTable: {},
};
const useMembershipsStore = createPageStore("memberships", initialState);

const EditGroupDetails = () => {
  const { groupId } = useParams();
  const [activeTab, setActiveTab] = useState("general");
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  //used for editing state
  const [isEditingGroupName, setIsEditingGroupName] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { user } = useUserStore2();
  // const { communityId } = useUserStore();
  const userId = user?.id;
  const [memberName, setMemberName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * @typedef {Object} MembershipsTableStore
   * @property {MembershipsByGroup} page - Array of membership items in the table
   * @property {function} setPage - Function to update the memberships table data
   */

  /** @type {MembershipsTableStore} */
  const { page, setPage } = useMembershipsStore();

  /**
   * Checks if a specific group table is empty
   * @param {string} id - The ID of the group to check
   * @returns {boolean} - Returns true if the table is empty or doesn't exist, false otherwise
   */
  const isGroupTableEmpty = (id) => {
    if (!page.membershipsTable) return true;
    if (!page.membershipsTable.hasOwnProperty(id)) return true;
    return page.membershipsTable[id].length === 0;
  };

  const actions = (row) => [
    {
      label: "View Details",
      icon: <FaEye />,
      onClick: () => {
        setSelectedMember(row); // Pass the row data as the selected member
        setShowViewModal(true); // Open the modal
      },
      variant: "dropPlain",
    },

    {
      label: "Edit Details",
      icon: <FaPencilAlt />,
      onClick: () => {
        setSelectedMember(row); // Pass the row data as the selected member
        setShowEditModal(true); // Open the modal
      },
      variant: "dropPlain",
    },
    {
      label: "Suspend User",
      icon: <FaExclamationTriangle />,
      onClick: () => {
        setSelectedMember(row);
        setIsOpen(true);
      },
      variant: "dropRed",
    },
    {
      label: "Remove User",
      icon: <FaTrashAlt />,
      onClick: () => {
        setSelectedMember(row);
        setShowDeleteModal(true);
      },

      variant: "dropRed",
    },
  ];

  const handleConfirmSuspend = async () => {
    if (!selectedMember) return;

    const payload = {
      MembershipId: selectedMember.membershipId,
      CommunityId: selectedMember.communityId,
      ProfileId: selectedMember.profileId,
      Status: "S",
    };

    try {
      await membershipService.editMembershipDetails(payload);
      setIsOpen(false);
      // Optionally, refresh the membership list if needed
      setReload((prev) => !prev);
    } catch (error) {
      console.error("Failed to suspend membership:", error);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedMember?.membershipId) {
      console.error("No membership ID found for deletion.");
      return;
    }

    try {
      setIsDeleting(true); // Optional: Set a loading state if needed
      await membershipService.removeMembership(selectedMember.membershipId);
      console.log("Member successfully removed:", selectedMember.membershipId);

      setShowDeleteModal(false);
      setReload((prev) => !prev);
    } catch (error) {
      console.error("Error removing membership:", error);
    } finally {
      setIsDeleting(true);
    }
  };

  const handleStatusChange = (status) => {
    setSelectedMember((prevState) => ({
      ...prevState,
      status: status,
    }));
  };

  const handleSaveChanges = async () => {
    if (!selectedMember) return;

    setLoading(true);

    const statusCode = selectedMember.status === "Active" ? "A" : "S"; // "A" for Active, "S" for Suspended

    const payload = {
      MembershipId: selectedMember.membershipId,
      CommunityId: selectedMember.communityId,
      ProfileId: selectedMember.profileId,
      Status: statusCode,
    };

    try {
      await membershipService.editMembershipDetails(payload);
      setShowEditModal(false);
      // Optionally, refresh the membership list if needed
      setReload((prev) => !prev);
    } catch (error) {
      console.error("Failed to update membership:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async () => {
    if (!memberName || !email || !selectedRole) {
      setError("All fields are required.");
      return;
    }

    setLoading(true);
    setError(null);

    const inviteData = {
      Email: email[0],
      Role: selectedRole,
      Name: memberName,
      CommunityId: communityId,
      GroupId: groupId,
    };

    try {
      const response = await invitationService.sendInvite(inviteData);
      console.log("Invite Sent Successfully:", response);
      setShowAddModal(false); // Close modal on success
      setReload((prev) => !prev);
    } catch (error) {
      setError("Failed to send invite. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };

  // const dataMap = (item) => ({
  //   membershipId: item?._id,
  //   communityId: item?.community._id,
  //   profileId: item?.profile?._id || "N/A",
  //   profileName: item?.profile?.name || "N/A",
  //   entityName: item?.entity?.name || "N/A",
  //   email: item?.entity?.email || "N/A",
  //   status:
  //     { A: "Active", S: "Suspended", P: "Pending", D: "Deactivated" }[
  //       item?.status
  //     ] || "N/A",
  //   lastLogin: item?.entity?.last_login
  //     ? new Date(item?.entity.last_login).toLocaleString()
  //     : "N/A",
  //   groupName: item?.group?.name || "N/A",
  //   groupDateCreated: item?.group?.create_date
  //     ? new Date(item?.group.create_date).toLocaleDateString()
  //     : "N/A",
  //   groupDateUpdated: item?.group?.last_update
  //     ? new Date(item?.group.last_update).toLocaleString()
  //     : "N/A",
  //   dateCreated: item?.create_date
  //     ? new Date(item?.create_date).toLocaleDateString()
  //     : "N/A",
  //   dateUpdated: item?.last_update
  //     ? new Date(item?.last_update).toLocaleString()
  //     : "N/A",
  //   groupId: item?.group?._id || "N/A",
  //   groupLeader: item?.group?.leaders?.length
  //     ? item?.group.leaders.join(", ")
  //     : "N/A",
  // });

  const [reload, setReload] = useState(false);

  const getGroups = ServiceOnMount(
    () => membershipService.getMembersByGroup(groupId),
    membershipDataMap,
    [reload]
  );

  const [communityId, setCommunityId] = useState(null);

  // Update communityId when members data loads
  useEffect(() => {
    if (getGroups?.data?.length > 0) {
      // All memberships will have the same community ID, so we can just take it from the first one
      setCommunityId(getGroups.data[0].communityId);
    }
  }, [getGroups?.data]);

  const [groupName, setGroupName] = useState("");

  if (getGroups?.data)
    setPage((state) => {
      state.membershipsTable[groupId] = getGroups.data;
    });

  // Update groupName once getGroups has data
  useEffect(() => {
    if (getGroups?.data?.length > 0) {
      setGroupName(getGroups?.data[0]?.groupName || "");
    }
  }, [getGroups?.data]);

  const handleSaveGroupName = async () => {
    setIsSaving(true);
    try {
      await groupService.editGroup({
        EntityId: userId,
        GroupId: groupId,
        CommunityId: communityId,
        Name: groupName,
      });

      setReload((prev) => !prev);
      toast.success("🎉 Group name updated successfully!", {
        autoClose: 2000,
      });
      setIsEditingGroupName(false);
    } catch (error) {
      console.error("Failed to update group name:", error);
      toast.error("❌ Error updating group name", {
        autoClose: 2000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmailChange = (newEmails) => {
    // Set the emails when they change
    setEmail(newEmails);
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen ">
      <div className="flex-1 flex flex-col">
        <div className="p-8 flex flex-col">
          <div className="bg-white shadow p-4 rounded-lg">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6 mb-6">
              {/* Left side: Image + Group Info */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full">
                <div className="flex items-center justify-center w-16 h-16 bg-indigo-500 rounded-full shrink-0">
                  <GoPeople className="text-white w-12 h-12 p-2" />
                </div>
                <div className="w-full">
                  <h1 className="text-lg font-semibold text-[#111827]">
                    {!isGroupTableEmpty(groupId)
                      ? page?.membershipsTable[groupId][0].groupName
                      : "Group Details"}
                  </h1>
                  <hr className="my-2" />

                  {/* Date Info Section */}
                  <div className="mt-4 flex flex-col sm:flex-row sm:space-x-8 space-y-4 sm:space-y-0">
                    {/* Date Created */}
                    <div>
                      <div className="text-sm font-semibold text-[#6B7280]">
                        Date Created
                      </div>
                      <div className="text-sm text-[#111827]">
                        {getGroups.loading && isGroupTableEmpty(groupId) ? (
                          <Skeleton className="w-28 h-5" />
                        ) : !isGroupTableEmpty(groupId) ? (
                          new Date(
                            page?.membershipsTable[groupId][0].groupDateCreated
                          ).toLocaleString()
                        ) : (
                          "N/A"
                        )}
                      </div>
                    </div>

                    {/* Date Updated */}
                    <div>
                      <div className="text-sm font-semibold text-[#6B7280]">
                        Date Updated
                      </div>
                      <div className="text-sm text-[#111827]">
                        {getGroups.loading && isGroupTableEmpty(groupId) ? (
                          <Skeleton className="w-28 h-5" />
                        ) : !isGroupTableEmpty(groupId) ? (
                          new Date(
                            page?.membershipsTable[groupId][0].groupDateUpdated
                          ).toLocaleString()
                        ) : (
                          "N/A"
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side: Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full md:w-auto justify-end md:justify-start">
                {isEditingGroupName ? (
                  <>
                    <Button
                      variant={"hollowGreen"}
                      onClick={() => setIsEditingGroupName(false)}
                      className="w-full sm:w-auto"
                    >
                      <FaTimes />
                      <span className="ml-2">Cancel</span>
                    </Button>

                    <Button
                      onClick={handleSaveGroupName}
                      disabled={isSaving}
                      className="w-full sm:w-auto flex items-center justify-center"
                    >
                      {isSaving ? (
                        <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4" />
                      ) : (
                        <FaSave />
                      )}
                      <span className="ml-2">
                        {isSaving ? "Saving..." : "Save"}
                      </span>
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => setIsEditingGroupName(true)}
                    className="w-full sm:w-auto flex items-center justify-center"
                  >
                    <FaPencilAlt />
                    <span className="ml-2">Edit</span>
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="flex">
            <button
              onClick={() => handleTabSwitch("general")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "general"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              General Info
            </button>
          </div>

          {/* Details Section */}
          {activeTab === "general" && (
            <div className="grid grid-cols-1 md:grid-cols-2 text-sm gap-6 bg-white md:max-w-[calc(100vw-375px)] shadow rounded-lg rounded-t-none p-4 md:p-6">
              {/* Left Section */}
              <div className="space-y-2">
                {[
                  {
                    label: "Group Name",
                    value:
                      getGroups.loading && isGroupTableEmpty(groupId) ? (
                        <Skeleton className="w-28 h-5" />
                      ) : (
                        <div className="flex items-center gap-2">
                          {isEditingGroupName ? (
                            <input
                              type="text"
                              className="border border-gray-300 rounded px-2 py-1 w-full"
                              value={groupName}
                              onChange={(e) => setGroupName(e.target.value)}
                            />
                          ) : (
                            <span>
                              {!isGroupTableEmpty(groupId)
                                ? page?.membershipsTable[groupId][0].groupName
                                : "N/A"}
                            </span>
                          )}
                        </div>
                      ),
                  },
                ].map((item, index) => (
                  <div
                    key={item.label}
                    className={`flex flex-col sm:flex-row py-2 ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <div className="sm:w-1/3 font-semibold text-gray-600 mb-1 sm:mb-0">
                      {item.label}
                    </div>
                    <div className="sm:w-2/3 text-gray-900">{item.value}</div>
                  </div>
                ))}
              </div>

              {/* Right Section */}
              <div className="space-y-2">
                {[
                  {
                    label: "Group Leader",
                    value: (
                      <div className="text-[#111827]">
                        {getGroups.loading && isGroupTableEmpty(groupId) ? (
                          <Skeleton className="w-28 h-5" />
                        ) : !isGroupTableEmpty(groupId) ? (
                          page?.membershipsTable[groupId][0].groupLeader ||
                          "No leader"
                        ) : (
                          "No leader"
                        )}
                      </div>
                    ),
                  },
                ].map((item, index) => (
                  <div
                    key={item.label}
                    className={`flex flex-col sm:flex-row py-2 ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <div className="sm:w-1/3 font-semibold text-gray-600 mb-1 sm:mb-0">
                      {item.label}
                    </div>
                    <div className="sm:w-2/3 text-gray-900">{item.value}</div>
                  </div>
                ))}
              </div>

              {/* Full Width Table Section */}
              <div className="col-span-1 md:col-span-2 space-y-4 bg-white shadow border rounded-lg p-4 md:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4">
                  <h1 className="text-lg font-semibold">Group Members List</h1>
                  {isEditingGroupName && (
                    <Button
                      onClick={() => setShowAddModal(true)}
                      className="flex items-center px-4 py-2 bg-[#14B8A6] text-sm sm:text-base text-white rounded hover:bg-[#2DD4BF]"
                    >
                      <FaPlus className="mr-2" /> Invite Member
                    </Button>
                  )}
                </div>
                <hr />
                <div className="overflow-x-auto w-full">
                  {getGroups.loading && isGroupTableEmpty(groupId) ? (
                    <div className="flex justify-center items-center py-6">
                      <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="ml-2 text-gray-600">
                        Loading Group Members...
                      </span>
                    </div>
                  ) : (
                    <DataTable
                      columns={columns}
                      data={page?.membershipsTable[groupId] || []}
                      actions={actions}
                      disableCheckboxes={true}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Modal - done*/}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Invite Member"
        actions={
          <div className="flex justify-between w-full">
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleInviteMember} disabled={loading}>
              {loading ? "Adding..." : "Add Member"}
            </Button>
          </div>
        }
      >
        <hr />
        <Label className="mt-4 text-left">Member Name:</Label>
        <Input
          placeholder="Enter member name"
          value={memberName}
          onChange={(e) => setMemberName(e.target.value)}
        />

        <Label className="mt-4 text-left">Email Address:</Label>
        {/* <Input
          placeholder="Enter email address"
          className="mb-6"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        /> */}

        <MultiEmailSelect
          selectedEmails={email} // Pass current email value
          onChange={handleEmailChange} // Update emails on change
          isDisabled={false} // Set to true if you want to disable the input
          isMulti={false}
          className="text-left"
        />

        <Label className="mt-4 text-left">Profile Role:</Label>
        <Select
          className="mb-6"
          value={selectedRole}
          onValueChange={setSelectedRole}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="U">User</SelectItem>
            <SelectItem value="CA">Community Admin</SelectItem>
            <SelectItem value="GL">Group Leader</SelectItem>
          </SelectContent>
        </Select>

        {/* Show error message if needed */}
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      </Modal>

      {/* View Modal - done */}
      {showViewModal && selectedMember && (
        <Modal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          title="View Member Details"
          description="Below are the details of the selected member."
        >
          {/* Member ID, Date Created, Date Updated */}
          <div className="bg-green-100 p-4 rounded-lg mb-4 grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Membership ID */}
            <div>
              <p className="text-sm font-semibold text-gray-700">
                Membership ID
              </p>
              <p className="text-base text-gray-900">
                <ShortenedText text={selectedMember.membershipId} />
              </p>
            </div>

            {/* Date Created */}
            <div>
              <p className="text-sm font-semibold text-gray-700">
                Date Created
              </p>
              <p className="text-base text-gray-900">
                {
                  new Date(selectedMember.createDate)
                    .toLocaleString()
                    .split(",")[0]
                }
              </p>
            </div>

            {/* Date Updated */}
            <div className="col-span-2 md:col-span-1">
              <p className="text-sm font-semibold text-gray-700">
                Date Updated
              </p>
              <p className="text-base text-gray-900">
                {
                  new Date(selectedMember.lastUpdate)
                    .toLocaleString()
                    .split(",")[0]
                }
              </p>
            </div>
          </div>

          <div className="text-left">
            {[
              { label: "Status", value: selectedMember.status },
              { label: "User Name", value: selectedMember.profileName },
              { label: "User Email", value: selectedMember.email },
              {
                label: "Profile",
                value: (
                  <ProfileCard
                    name={selectedMember.profileName}
                    profileId={
                      <ShortenedText text={selectedMember.profileId} />
                    }
                    createDate={
                      new Date(selectedMember.profileCreateDate)
                        .toLocaleString()
                        .split(",")[0]
                    }
                    variant="teal"
                  />
                ),
              },
            ].map((item, index) => (
              <div
                key={item.label}
                className={`flex items-center flex-wrap px-4 py-2 ${
                  index % 2 === 0 ? "bg-gray-100" : "bg-white"
                }`}
              >
                <p className="text-sm font-semibold text-gray-700 w-[120px] min-w-[100px]">
                  {item.label}
                </p>

                <div className="text-base text-gray-900 flex-1 break-words">
                  {item.label === "Status" ? (
                    <div
                      className={`inline-block px-3 py-1 text-sm font-semibold rounded-full ${
                        item.value === "Active"
                          ? "text-[#16A34A] bg-[#DCFCE7]"
                          : item.value === "Suspended"
                          ? "text-[#EF4444] bg-[#FEE2E2]"
                          : "text-[#CA8A04] bg-[#FEF9C3]"
                      }`}
                    >
                      {item.value}
                    </div>
                  ) : (
                    item.value
                  )}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Edit Modal - done */}
      {showEditModal && selectedMember && (
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Edit Member Details"
          description="Below are the details of the selected member. You can edit the status here."
        >
          {/* Member ID, Date Created, Date Updated */}
          <div className="bg-green-100 p-4 rounded-lg mb-4 grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Member ID */}
            <div>
              <p className="text-sm font-semibold text-gray-700">
                Membership ID
              </p>
              <p className="text-base text-gray-900">
                <ShortenedText text={selectedMember.membershipId} />
              </p>
            </div>

            {/* Date Created */}
            <div>
              <p className="text-sm font-semibold text-gray-700">
                Date Created
              </p>
              <p className="text-base text-gray-900">
                {
                  new Date(selectedMember.createDate)
                    .toLocaleString()
                    .split(",")[0]
                }
              </p>
            </div>

            {/* Date Updated */}
            <div className="col-span-2 md:col-span-1">
              <p className="text-sm font-semibold text-gray-700">
                Date Updated
              </p>
              <p className="text-base text-gray-900">
                {
                  new Date(selectedMember.lastUpdate)
                    .toLocaleString()
                    .split(",")[0]
                }
              </p>
            </div>
          </div>

          {/* Status, Username, Email, Profile */}
          <div className="text-left">
            {[
              {
                label: "Status",
                value: (
                  <div className="flex space-x-0">
                    {["Active", "Suspended"].map((statusOption) => (
                      <button
                        key={statusOption}
                        onClick={() => handleStatusChange(statusOption)}
                        className={`w-full px-4 py-2 border font-semibold text-sm ${
                          selectedMember.status === statusOption
                            ? statusOption === "Suspended"
                              ? "bg-red-500 text-white border-red-500 hover:bg-red-600"
                              : "bg-green-500 text-white border-green-500 hover:bg-green-600"
                            : "bg-white text-black border-gray-300 hover:bg-gray-100"
                        }`}
                      >
                        {statusOption}
                      </button>
                    ))}
                  </div>
                ),
              },
              { label: "User Name", value: selectedMember.entityName },
              { label: "User Email", value: selectedMember.email },
              {
                label: "Profile",
                value: (
                  <ProfileCard
                    name={selectedMember.profileName}
                    profileId={
                      <ShortenedText text={selectedMember.profileId} />
                    }
                    createDate={
                      new Date(selectedMember.profileCreateDate)
                        .toLocaleString()
                        .split(",")[0]
                    }
                    variant="teal"
                  />
                ),
              },
            ].map((item, index) => (
              <div
                key={item.label}
                className={`flex items-center flex-wrap px-4 py-2 ${
                  index % 2 === 0 ? "bg-gray-100" : "bg-white"
                }`}
              >
                <p className="text-sm font-semibold text-gray-700 w-[120px] min-w-[100px]">
                  {item.label}
                </p>
                <div className="text-base text-gray-900 flex-1 break-words">
                  {item.value}
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end mt-4 space-x-4">
            <Button
              variant="secondary"
              onClick={() => setShowEditModal(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveChanges} disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </Modal>
      )}

      {/* Suspend Modal -done */}
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Suspend Member"
        description="Are you sure you want to suspend this member?"
        icon={<FaExclamationTriangle className="text-red-500" />}
        actions={
          <div className="flex justify-between w-full">
            <Button variant={"secondary"} onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={handleConfirmSuspend}
              disabled={loading}
            >
              {loading ? "Suspending..." : "Suspend"}
            </Button>
          </div>
        }
      ></Modal>

      {/* Delete Modal - done */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Remove Member"
        description="Are you sure you want to remove this member? This action is NOT reversible and will result in permanent loss of this member."
        icon={<FaExclamationTriangle className="text-red-500" />}
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowDeleteModal(false)}
            >
              No, Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Removing..." : "Yes, remove this member"}
            </Button>
          </div>
        }
      />
    </div>
  );
};

export default EditGroupDetails;
